import {
  NavBackend, NavSurfaces, NavIntegrations, NavClients,
  NavCoverage, NavIncidents, NavCicd, NavDashboards, NavDora, NavGraph, NavTools,
  StatSlos, StatAlerts, NavInfra, NavScorecard, ScrollText, StatFreshness,
  TrendingUp, Activity, NavDatabase, Zap, Globe, NavShield, NavCost, NavAi, NavRenewals,
} from "./components/Icons";
import type { ComponentType } from "react";

export type NavId =
  | "backend" | "surfaces" | "integrations" | "clients"
  | "scorecard" | "slo" | "coverage" | "infra" | "logs" | "alert-health"
  | "incidents" | "cicd" | "dashboards" | "dora" | "graph" | "tools" | "renewals"
  | "platform-kpis" | "traces" | "data-stores" | "runtime" | "experience"
  | "security" | "cost" | "ai-agents" | "data-pipelines";

export interface NavItem {
  id: NavId;
  label: string;
  desc: string;
  path: string;
  Icon: ComponentType<{ className?: string; size?: number }>;
  shortcut?: string;
}

export const ESTATES: NavItem[] = [
  { id: "backend",      path: "/backend",      label: "Backend",      Icon: NavBackend,      shortcut: "1",
    desc: "Java/Spring + Python services running in the waffy-api namespace" },
  { id: "surfaces",     path: "/surfaces",     label: "Surfaces",     Icon: NavSurfaces,     shortcut: "2",
    desc: "Web apps + mobile (iOS / Android) — what users actually touch" },
  { id: "integrations", path: "/integrations", label: "Integrations", Icon: NavIntegrations, shortcut: "3",
    desc: "Outbound dependencies — payment gateways, KYC, partners we call" },
  { id: "clients",      path: "/clients",      label: "Clients",      Icon: NavClients,      shortcut: "4",
    desc: "Inbound API consumers — partners, SDK consumers, end-user cohorts" },
];

export const OPERATIONS: NavItem[] = [
  { id: "scorecard",  path: "/scorecard",  label: "Scorecard",  Icon: NavScorecard,
    desc: "Ops-excellence maturity grade per service — coverage + SLO + DORA, worst first" },
  { id: "slo",        path: "/slo",        label: "SLOs",       Icon: StatSlos,
    desc: "Service-level objectives & error budgets — live burn from Prometheus" },
  { id: "coverage",   path: "/coverage",   label: "Coverage",   Icon: NavCoverage,   shortcut: "5",
    desc: "Observability gaps across all four estates — drives the punch list" },
  { id: "infra",      path: "/infra",      label: "Infrastructure", Icon: NavInfra,
    desc: "Cluster nodes & Kubernetes workload health from Prometheus" },
  { id: "logs",       path: "/logs",       label: "Logs",       Icon: ScrollText,
    desc: "Recent application logs from Loki — filter by service, level, window" },
  { id: "alert-health", path: "/alert-health", label: "Alert Health", Icon: StatAlerts,
    desc: "Firing alerts, runbook & owner coverage, and the noisiest signals (24h)" },
  { id: "runtime",    path: "/runtime",    label: "Runtime & Queues", Icon: Zap,
    desc: "JVM heap/GC, Hikari pool saturation, Kafka controller health" },
  { id: "experience", path: "/experience", label: "User Experience", Icon: Globe,
    desc: "Mobile crash-free (Crashlytics) + synthetic uptime probes" },
  { id: "renewals",   path: "/renewals",   label: "Renewals",   Icon: NavRenewals,
    desc: "Certificate expiry, partner integration keys, and vendor contracts & spend" },
  { id: "incidents",  path: "/incidents",  label: "Incidents",  Icon: NavIncidents,  shortcut: "6",
    desc: "Active firing alerts + recent deploys joined onto one timeline" },
  { id: "cicd",       path: "/cicd",       label: "CI/CD",      Icon: NavCicd,       shortcut: "7",
    desc: "Currently-running builds, awaiting-approval gates, recent failures, per-pipeline success" },
  { id: "dashboards", path: "/dashboards", label: "Dashboards", Icon: NavDashboards, shortcut: "8",
    desc: "Every Grafana dashboard, foldered. Per-service golden-signals auto-resolved" },
  { id: "dora",       path: "/dora",       label: "DORA",       Icon: NavDora,       shortcut: "9",
    desc: "Deploy frequency, lead time, change failure rate, MTTR — overall + per team" },
  { id: "graph",      path: "/graph",      label: "Graph",      Icon: NavGraph,      shortcut: "0",
    desc: "Service dependency graph — click any node to highlight blast radius" },
  { id: "tools",      path: "/tools",      label: "Tools",      Icon: NavTools,
    desc: "External tools and bookmarks (Grafana, Jenkins, Jira, …)" },
];

// Planned platform-wide observability domains (Wave 2) — gated on external data sources;
// each renders a "planned" ScaffoldTab until its dependency is connected.
export const PLATFORM: NavItem[] = [
  { id: "platform-kpis",  path: "/platform-kpis",  label: "Platform KPIs",  Icon: TrendingUp,
    desc: "Business & money observability from ClickHouse" },
  { id: "traces",         path: "/traces",         label: "Traces",         Icon: Activity,
    desc: "Distributed tracing (Tempo) — the third pillar" },
  { id: "data-stores",    path: "/data-stores",    label: "Data Stores",    Icon: NavDatabase,
    desc: "Postgres / Mongo / ClickHouse / Redis health" },
  { id: "security",       path: "/security",       label: "Security",       Icon: NavShield,
    desc: "Auth health, certs, secrets, SAMA/KYC posture" },
  { id: "cost",           path: "/cost",           label: "Cost",           Icon: NavCost,
    desc: "Cloud spend & capacity by service" },
  { id: "ai-agents",      path: "/ai-agents",      label: "AI Agents",      Icon: NavAi,
    desc: "LLM cost/latency/errors for the agent platform" },
  { id: "data-pipelines", path: "/data-pipelines", label: "Data Pipelines", Icon: StatFreshness,
    desc: "Airbyte / DataFlow → ClickHouse freshness & jobs" },
];

export const ALL_NAV: NavItem[] = [...ESTATES, ...OPERATIONS, ...PLATFORM];
